import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import DetailItem from "../common/DetailItem";
import { CreditNoteResponseDto } from "../../shared/dtos/creditNote/CreditNoteResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";

interface CreditNoteDetailsTabProps {
  note: CreditNoteResponseDto;
}

const CreditNoteDetailsTab: React.FC<CreditNoteDetailsTabProps> = ({
  note,
}) => {
  const { header, items, refundTransactions, charges, taxBreakdowns } = note;
  const currency = note.currencyCode || "KES";

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Customer & Reason
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <DetailItem
                label="Customer Name"
                value={note.customerName || "Walk-in Customer"}
              />
              <Box sx={{ mt: 2 }}>
                <DetailItem label="Reason for Return" value={note.reason} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <DetailItem
                  label="Against Invoice #"
                  value={note.invoiceNumber || "N/A"}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <DetailItem
                  label="Against Receipt #"
                  value={note.receiptTransactionNumber || "N/A"}
                />
              </Box>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Issued By
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {header?.companySnapshot?.companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PIN: {header?.companySnapshot?.taxId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {header?.companySnapshot?.address}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
            bgcolor: "background.paper",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Returned Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Disc/Unit</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {item.name}
                        </Typography>
                        {item.taxCode && (
                          <Typography variant="caption" color="text.secondary">
                            Tax: {item.taxCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{item.qty}</TableCell>
                      <TableCell align="right">
                        {formatPlainNumber(item.price)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "error.main" }}>
                        {item.discount > 0
                          ? formatPlainNumber(item.discount)
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatPlainNumber(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                bgcolor: "background.paper",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Refund Transactions
                </Typography>
                {refundTransactions && refundTransactions.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {refundTransactions.map((rf) => (
                          <TableRow key={rf.id}>
                            <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {rf.paymentMethodName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {new Date(rf.createdAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: "none",
                                pr: 0,
                                fontWeight: "bold",
                                color: "error.main",
                              }}
                            >
                              {formatPlainNumber(rf.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    No refunds processed. Value kept as store credit.
                  </Typography>
                )}

                {/* --- APPLIED PENALTIES / CHARGES --- */}
                {charges && charges.length > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: "1px dashed rgba(255,255,255,0.1)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      Applied Penalties & Charges
                    </Typography>
                    {charges.map((charge, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {charge.name} ({charge.rate}% - {charge.chargeType})
                        </Typography>
                        <Typography
                          variant="body2"
                          color="error.main"
                          sx={{ fontWeight: "bold" }}
                        >
                          - {formatPlainNumber(charge.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* --- TAX BREAKDOWN --- */}
                {taxBreakdowns && taxBreakdowns.length > 0 && (
                  <Box
                    sx={{
                      mt: charges && charges.length > 0 ? 1 : 3,
                      pt: charges && charges.length > 0 ? 1 : 2,
                      borderTop:
                        charges && charges.length > 0
                          ? "none"
                          : "1px dashed rgba(255,255,255,0.1)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      Tax Reversal Breakdown
                    </Typography>
                    {taxBreakdowns.map((tax) => (
                      <Box
                        key={tax.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {tax.name} ({tax.rate}%)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {formatPlainNumber(tax.taxAmount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Summary
                </Typography>

                {/* Currency and Exchange Rate */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Currency
                  </Typography>
                  <Typography
                    variant="body2"
                    color="secondary.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {currency}
                  </Typography>
                </Box>

                {Number(note.exchangeRate) !== 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Exchange Rate
                    </Typography>
                    <Typography
                      variant="body2"
                      color="secondary.main"
                      sx={{ fontWeight: "bold" }}
                    >
                      {note.exchangeRate}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Gross Return Value
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    {formatPlainNumber(note.subtotal)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Tax
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    {formatPlainNumber(note.totalTax)}
                  </Typography>
                </Box>

                {/* Dynamically calculate and show total deductions from charges */}
                {charges && charges.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="error.main">
                      Less Penalties
                    </Typography>
                    <Typography
                      variant="body2"
                      color="error.main"
                      sx={{ fontWeight: "500" }}
                    >
                      -{" "}
                      {formatPlainNumber(
                        charges.reduce((acc, c) => acc + c.amount, 0),
                      )}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Grand Total - Explcitly denoting Currency */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Net Credit Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {currency} {formatPlainNumber(note.total)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Amount Refunded/Used
                  </Typography>
                  <Typography
                    variant="body2"
                    color="error.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatPlainNumber(note.amountUsed)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    p: 1,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    mt: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Available Credit Balance
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatPlainNumber(note.total - note.amountUsed)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default CreditNoteDetailsTab;
